// src/pages/AllUser.js
import React, { useEffect, useState } from "react";
import {
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
  FaUser,
  FaLock,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa";
import { postData } from "../../../services/FetchNodeservices";

export default function AllUser() {
  const [users, setUsers] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [search, setSearch] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [page, setPage] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("view");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  const rowsPerPage = 14;

  /* ================= FETCH ================= */
  const fetchUsers = async () => {
    const res = await postData("users/fetch", {});
    if (res?.status) {
      setUsers(
        res.data.map((u) => ({
          id: u.id,
          employeeCode: u.employee_code,
          firstName: u.first_name,
          lastName: u.last_name,
          name: `${u.first_name} ${u.last_name}`.trim(),
          role: Array.isArray(u.designations)
            ? u.designations.join(", ")
            : u.designations || "-",
          email: u.email,
          phone: u.phone || u.mobile_num || "-",
          designation_id: u.designation_id || null,
          isActive: u.is_active !== undefined ? !!u.is_active : true,
        }))
      );
    }
  };

  const fetchDesignations = async () => {
    const res = await postData("users/fetchDesignations", {});
    if (res?.status) setDesignations(res.data);
  };

  const fetchNextEmployeeCode = async () => {
    const res = await postData("users/nextEmployeeCode", {});
    if (res?.status) {
      setFormData((p) => ({ ...p, employeeCode: res.employee_code }));
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDesignations();
  }, []);

  /* ================= FILTER ================= */
  const filtered = users.filter((u) => {
    const term = search.toLowerCase();
    const matchesSearch =
      u.name.toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.employeeCode || "").toLowerCase().includes(term);

    const matchesDesignation =
      designationFilter === "" || (u.role || "").includes(designationFilter);

    return matchesSearch && matchesDesignation;
  });

  const pageCount = Math.ceil(filtered.length / rowsPerPage) || 1;
  const displayed = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  /* ================= MODALS ================= */
  const openAddModal = async () => {
    setModalType("add");
    setFormData({
      employeeCode: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      designation_id: "",
    });
    setModalOpen(true);
    await fetchNextEmployeeCode();
  };

  const openEditModal = (u) => {
    setModalType("edit");
    setFormData({ ...u, password: "" });
    setModalOpen(true);
  };

  const openViewModal = (u) => {
    setModalType("view");
    setFormData({ ...u });
    setModalOpen(true);
  };

  const openDeleteModal = (u) => {
    setModalType("delete");
    setFormData({ ...u });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData({});
  };

  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* ================= TOGGLE ================= */
  const toggleStatusBackend = async (u) => {
    const res = await postData(`users/toggleStatus/${u.id}`, {
      is_active: u.isActive ? 0 : 1,
    });
    if (res?.status) {
      setUsers((prev) =>
        prev.map((x) =>
          x.id === u.id ? { ...x, isActive: !u.isActive } : x
        )
      );
    }
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        employee_code: formData.employeeCode,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        mobile_num: formData.phone,
        password: formData.password,
        designation_id: formData.designation_id || null,
      };

      let res;
      if (modalType === "add") res = await postData("users/add", payload);
      if (modalType === "edit")
        res = await postData(`users/update/${formData.id}`, payload);
      if (modalType === "delete")
        res = await postData(`users/delete/${formData.id}`, {});

      if (res?.status) {
        await fetchUsers();
        closeModal();
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pb-2 border-b border-slate-100">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
            Employees & Users
          </h2>
          <p className="text-xs text-slate-500 mt-1">Manage team roles, access, and login permissions</p>
        </div>

        <button
          onClick={openAddModal}
          className="
            inline-flex items-center justify-center gap-2
            px-4 py-2.5
            bg-indigo-600 text-white text-sm font-semibold
            rounded-xl shadow-md hover:bg-indigo-750 hover:shadow-lg transition-all duration-200
            w-fit
          "
        >
          <FaPlus size={12} /> Add Employee
        </button>
      </div>

      {/* SEARCH + FILTER */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative w-full sm:w-72">
          <FaSearch className="absolute top-3.5 left-3 text-slate-400 text-sm" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or code..."
            className="glass-input pl-10 pr-3 py-2.5 rounded-xl outline-none transition-all w-full text-sm"
          />
        </div>

        <select
          value={designationFilter}
          onChange={(e) => setDesignationFilter(e.target.value)}
          className="glass-input px-4 py-2.5 rounded-xl text-sm outline-none transition-all w-full sm:w-auto font-medium"
        >
          <option value="">All Designations</option>
          {designations.map((d) => (
            <option key={d.id} value={d.designation_name}>
              {d.designation_name}
            </option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="glass-card overflow-x-auto">
        <table className="min-w-[950px] w-full text-xs">
          <thead>
            <tr className="bg-slate-50/75 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <th className="px-6 py-3.5 text-center w-16">Sr</th>
              <th className="px-6 py-3.5 text-left">Code</th>
              <th className="px-6 py-3.5 text-left">Name</th>
              <th className="px-6 py-3.5 text-left">Designation</th>
              <th className="px-6 py-3.5 text-left">Email</th>
              <th className="px-6 py-3.5 text-left">Phone</th>
              <th className="px-6 py-3.5 text-center w-32">Status</th>
              <th className="px-6 py-3.5 text-center w-40">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayed.length > 0 ? (
              displayed.map((u, i) => (
                <tr key={u.id} className="hover:bg-slate-50/40 transition-colors duration-150 border-b border-slate-100 last:border-0">
                  <td className="px-6 py-3.5 text-center text-slate-500 whitespace-nowrap">
                    {(page - 1) * rowsPerPage + i + 1}
                  </td>
                  <td className="px-6 py-3.5 text-slate-600 whitespace-nowrap font-medium">
                    {u.employeeCode}
                  </td>
                  <td className="px-6 py-3.5 font-bold text-slate-800 whitespace-nowrap">
                    {u.name}
                  </td>
                  <td className="px-6 py-3.5 text-slate-600 whitespace-nowrap font-medium">
                    {u.role}
                  </td>
                  <td className="px-6 py-3.5 text-slate-500 whitespace-nowrap">
                    {u.email}
                  </td>
                  <td className="px-6 py-3.5 text-slate-500 whitespace-nowrap">
                    {u.phone}
                  </td>
                  <td className="px-6 py-3.5 text-center whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider
                        ${
                          u.isActive
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200/45"
                            : "bg-rose-50 text-rose-700 border border-rose-200/45"
                        }`}
                    >
                      {u.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="flex justify-center items-center gap-1.5">
                      <button
                        onClick={() => openViewModal(u)}
                        title="View Details"
                        className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-150"
                      >
                        <FaEye size={13} />
                      </button>
                      <button
                        onClick={() => openEditModal(u)}
                        title="Edit User"
                        className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all duration-150"
                      >
                        <FaEdit size={13} />
                      </button>
                      <button
                        onClick={() => toggleStatusBackend(u)}
                        title={u.isActive ? "Deactivate" : "Activate"}
                        className={`p-2 rounded-lg transition-all duration-150
                          ${
                            u.isActive
                              ? "text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                              : "text-rose-400 hover:text-emerald-600 hover:bg-emerald-50"
                          }`}
                      >
                        {u.isActive ? (
                          <FaToggleOn size={15} />
                        ) : (
                          <FaToggleOff size={15} />
                        )}
                      </button>
                      <button
                        onClick={() => openDeleteModal(u)}
                        title="Delete User"
                        className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-150"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-8 text-slate-400 text-sm">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-between items-center mt-5 text-xs text-slate-500 font-medium">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="glass-btn px-3.5 py-1.5 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
        >
          Prev
        </button>
        <span>Page {page} of {pageCount}</span>
        <button
          disabled={page === pageCount}
          onClick={() => setPage(page + 1)}
          className="glass-btn px-3.5 py-1.5 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent"
        >
          Next
        </button>
      </div>

      {/* ================= MODALS ================= */}
      {modalOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-lg rounded-3xl p-7 shadow-2xl relative flex flex-col gap-5 animate-in fade-in zoom-in duration-200">
            
            {/* CLOSE BUTTON */}
            <button
              onClick={closeModal}
              className="absolute right-5 top-5 text-slate-400 hover:text-slate-700 p-1.5 rounded-full hover:bg-slate-50 transition-colors"
            >
              <FaTimes size={16} />
            </button>

            {/* HEADER */}
            <h2 className="text-xl font-bold text-slate-800 border-b border-slate-100 pb-3 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
              {modalType === "view" && "Employee Details"}
              {modalType === "edit" && "Edit Employee"}
              {modalType === "add" && "Add New Employee"}
              {modalType === "delete" && "Delete Employee"}
            </h2>

            {/* VIEW MODAL */}
            {modalType === "view" && (
              <div className="flex flex-col items-center gap-5 mt-2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center text-xl font-bold shadow-md">
                  {formData.firstName?.[0]}{formData.lastName?.[0]}
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-slate-800">{formData.name}</h3>
                  <p className="text-xs font-semibold text-slate-500 mt-0.5">{formData.role}</p>
                </div>

                <div className="w-full bg-slate-50 rounded-2xl p-4 border border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="flex items-center gap-2.5">
                    <span className="text-indigo-500"><FaUser size={13} /></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Employee Code</p>
                      <p className="font-semibold text-slate-800 mt-0.5">{formData.employeeCode}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="text-indigo-500"><FaEnvelope size={13} /></span>
                    <div className="truncate">
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Email Address</p>
                      <p className="font-semibold text-slate-800 mt-0.5 truncate" title={formData.email}>{formData.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="text-indigo-500"><FaPhone size={13} /></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Mobile Number</p>
                      <p className="font-semibold text-slate-800 mt-0.5">{formData.phone || "-"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="text-indigo-500"><FaBriefcase size={13} /></span>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Account Status</p>
                      <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider
                        ${
                          formData.isActive
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800"
                        }`}
                      >
                        {formData.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DELETE MODAL */}
            {modalType === "delete" && (
              <div className="flex flex-col items-center gap-3 mt-2 text-center">
                <span className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 shadow-inner">
                  <FaExclamationTriangle size={20} />
                </span>
                <div className="mt-1">
                  <h3 className="font-bold text-slate-800 text-base">Are you sure?</h3>
                  <p className="text-xs text-slate-500 mt-1.5 max-w-sm leading-relaxed">
                    You are about to delete employee <b>{formData.name}</b>. All associated data (attendance, assignments) will be removed.
                  </p>
                </div>
              </div>
            )}

            {/* ADD / EDIT MODAL */}
            {(modalType === "add" || modalType === "edit") && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs mt-2">
                <div className="flex flex-col sm:col-span-2">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Employee Code</label>
                  <input
                    readOnly
                    value={formData.employeeCode}
                    className="border border-slate-200 p-2.5 rounded-xl text-slate-500 font-semibold bg-slate-50 outline-none w-full"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">First Name</label>
                  <input
                    name="firstName"
                    value={formData.firstName || ""}
                    onChange={handleChange}
                    placeholder="e.g. Satish"
                    className="border border-slate-200 p-2.5 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Last Name</label>
                  <input
                    name="lastName"
                    value={formData.lastName || ""}
                    onChange={handleChange}
                    placeholder="e.g. Chauhan"
                    className="border border-slate-200 p-2.5 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full"
                  />
                </div>

                <div className="flex flex-col sm:col-span-2">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Email Address</label>
                  <div className="relative">
                    <FaEnvelope className="absolute top-3.5 left-3 text-slate-400 text-sm" />
                    <input
                      name="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      placeholder="e.g. satish.chauhan@morganplacement.com"
                      className="pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:col-span-2">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Mobile Number</label>
                  <div className="relative">
                    <FaPhone className="absolute top-3.5 left-3 text-slate-400 text-sm" />
                    <input
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                      placeholder="e.g. +91 9876543210"
                      className="pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:col-span-2">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Designation</label>
                  <div className="relative">
                    <FaBriefcase className="absolute top-3.5 left-3 text-slate-400 text-sm" />
                    <select
                      name="designation_id"
                      value={formData.designation_id || ""}
                      onChange={handleChange}
                      className="pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full bg-white font-medium text-slate-650"
                    >
                      <option value="">Select Designation</option>
                      {designations.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.designation_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col sm:col-span-2">
                  <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
                    Password {modalType === "edit" && "(Leave blank to keep unchanged)"}
                  </label>
                  <div className="relative">
                    <FaLock className="absolute top-3.5 left-3 text-slate-400 text-sm" />
                    <input
                      type="password"
                      name="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* BUTTONS */}
            <div className="flex justify-end gap-3 mt-4 border-t border-slate-100 pt-4 text-xs font-semibold">
              <button
                onClick={closeModal}
                className="px-4 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-slate-600 shadow-sm"
              >
                {modalType === "view" ? "Close" : "Cancel"}
              </button>
              {modalType !== "view" && (
                <button
                  onClick={handleSubmit}
                  className={`px-5 py-2.5 text-white rounded-xl shadow-md transition-all duration-150 hover:shadow-lg
                    ${
                      modalType === "delete"
                        ? "bg-rose-600 hover:bg-rose-700"
                        : "bg-indigo-600 hover:bg-indigo-750"
                    }`}
                >
                  {loading ? "Saving..." : modalType === "delete" ? "Delete User" : "Save Changes"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
